import type { Editor } from "@tiptap/core";

/**
 * Returns the editor's HTML with all agent staging artifacts stripped, so
 * `TiptapEditor.onUpdate` can persist only "accepted" content. Stripped:
 *
 *   • `<div data-agent-suggestion-block>` blocks — removed entirely. Their
 *     children are NOT preserved, because by definition the user has not
 *     accepted them. (Reloading a doc that was saved mid-staging shouldn't
 *     resurrect floating proposals with no agent session to drive them.)
 *
 *   • `<span data-agent-suggestion>` and `<span data-agent-pending-replace>`
 *     marks — the wrapper span is unwrapped, the text inside is preserved.
 *     These are staging *marks* on text the user already has; removing the
 *     mark leaves the text intact.
 *
 * The implementation goes through a DOMParser rather than walking PM nodes
 * because TiptapEditor's onUpdate is already HTML-shaped (it persists HTML to
 * the backend), so a single HTML→HTML pass keeps the contract identical and
 * avoids re-roundtripping through ProseMirror.
 */
export function getCleanHtml(editor: Editor): string {
    const raw = editor.getHTML();
    if (!raw) return raw;
    if (
        !raw.includes("data-agent-suggestion-block") &&
        !raw.includes("data-agent-suggestion") &&
        !raw.includes("data-agent-pending-replace")
    ) {
        return raw; // Fast path — no staging artifacts present.
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
    const root = doc.body.firstElementChild;
    if (!root) return raw;

    // Block-level suggestion wrappers: delete entirely (unaccepted content).
    root.querySelectorAll("[data-agent-suggestion-block]").forEach(el => {
        el.remove();
    });

    // Inline staging marks: unwrap (preserve text, drop the span).
    const unwrapSelectors = ["[data-agent-suggestion]", "[data-agent-pending-replace]"];
    unwrapSelectors.forEach(selector => {
        root.querySelectorAll(selector).forEach(el => {
            const parent = el.parentNode;
            if (!parent) return;
            while (el.firstChild) {
                parent.insertBefore(el.firstChild, el);
            }
            parent.removeChild(el);
        });
    });

    return root.innerHTML;
}
